import { IHighlight } from "@argument-studio/react-pdf-highlighter-with-categories";
import { User } from "@/types/models";
import apiClient from "./apiClient";
import axios from "axios";
import {
  Annotation,
  Canvas,
  CanvasAssessment,
  CanvasRubric,
  CanvasUserData,
  Category,
  CreateAnnotationRequest,
  CreateCanvasRequest,
  CreatePaperRequest,
  CreateUserRequest,
  Document,
  DocumentDependent,
  DocumentWithOwner,
  InviteTokenRedemption,
  Organization,
  OrganizationInviteToken,
  OrganizationMembershipWithUser,
  OrganizationWithRole,
  Paper,
  Reading,
  ReadingAssessment,
  ReadingRubric,
  ReadingWithUserStatus,
  UpdateAnnotationRequest,
  WorkspaceCanvasView,
  WorkspacePaperView,
  UpdateCanvasAssessmentRequest,
  UpdateCanvasRubricRequest,
  LogTimeSessionRequest,
  OnboardingStateResponse,
  TimeSessionsResponse,
  UpdateOnboardingStateRequest,
  UpdateReadingAssessmentRequest,
  UpdateReadingRubricRequest,
  UpdateWorkspaceRequest,
  Workspace,
  WorkspaceInviteRedemption,
  WorkspaceInviteToken,
  ResourceGrades,
  WorkspaceStudentGradebook,
} from "@/types/models";
import { CanvasData } from "@/types/workspace";
import { ApiResult, isAppError, createAppError } from "@/types/errors";

/**
 * Helper to handle errors in API calls
 */
function handleError(error: unknown): ApiResult<never> {
  if (isAppError(error)) {
    return { success: false, error };
  }
  return {
    success: false,
    error: createAppError(
      error instanceof Error ? error.message : String(error)
    ),
  };
}

/**
 * Creates a workspace and returns the full workspace (including server-applied
 * defaults). If `categories` is provided and non-empty, those override the
 * server-side defaults; otherwise the backend applies its default category set.
 */
export const createWorkspace = async (
  workspaceName: string,
  categories?: Category[],
  orgId?: string | null
): Promise<ApiResult<Workspace>> => {
  try {
    const body: {
      workspace_name: string;
      shared_data?: { categories: Category[] };
      org_id?: string;
    } = { workspace_name: workspaceName };
    if (categories && categories.length > 0) {
      body.shared_data = { categories };
    }
    if (orgId) {
      body.org_id = orgId;
    }
    const response = await apiClient.post<Workspace>("/workspaces", body);
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export const getLatestWorkspaceId = async (): Promise<ApiResult<string>> => {
  try {
    const { data } = await apiClient.get<Workspace[]>("/workspaces");
    if (data?.[0]) {
      return { success: true, data: data[0].workspace_id };
    }
    return { success: true, data: "" };
  } catch (error) {
    return handleError(error);
  }
};

export const getWorkspaceData = async (
  workspaceId: string
): Promise<ApiResult<Workspace>> => {
  try {
    const { data, status } = await apiClient.get<Workspace>(
      `/workspaces/${workspaceId}`
    );
    if (status !== 200) {
      return {
        success: false,
        error: createAppError("Failed to fetch workspace data", status),
      };
    }
    return { success: true, data };
  } catch (error) {
    return handleError(error);
  }
};

export const getWorkspaces = async (): Promise<ApiResult<Workspace[]>> => {
  try {
    const { data } = await apiClient.get<Workspace[]>("/workspaces");
    return { success: true, data };
  } catch (error) {
    return handleError(error);
  }
};

export const getCanvases = async (
  workspaceId: string
): Promise<ApiResult<Canvas[]>> => {
  try {
    const { data } = await apiClient.get<Canvas[]>(
      `/workspaces/${workspaceId}/canvases`
    );
    return { success: true, data };
  } catch (error) {
    return handleError(error);
  }
};

export const getReadings = async (
  workspaceId: string
): Promise<ApiResult<ReadingWithUserStatus[]>> => {
  try {
    const { data } = await apiClient.get<{ readings: ReadingWithUserStatus[] }>(
      `/workspaces/${workspaceId}/readings`
    );
    return { success: true, data: data.readings };
  } catch (error) {
    return handleError(error);
  }
};

/** Fetch specific reading with full document object */
export const fetchReading = async (
  readingId: string
): Promise<ApiResult<ReadingWithUserStatus>> => {
  try {
    const response = await apiClient.get<ReadingWithUserStatus>(
      `/readings/${readingId}`
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export type ResourceStatus =
  | "assigned"
  | "in_progress"
  | "completed"
  | "submitted"
  | "returned";

/** Update reading status for a user (self-only unless instructor). */
export const updateReadingStatus = async (
  readingId: string,
  userId: string,
  status: ResourceStatus
): Promise<ApiResult<void>> => {
  try {
    await apiClient.patch(`/readings/${readingId}/permissions/${userId}/status`, {
      status,
    });
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Update canvas status for a user. Canvas/paper status routes are
 * workspace-nested (unlike the top-level reading route), so these take
 * workspaceId.
 */
export const updateCanvasStatus = async (
  workspaceId: string,
  canvasId: string,
  userId: string,
  status: ResourceStatus
): Promise<ApiResult<void>> => {
  try {
    await apiClient.patch(
      `/workspaces/${workspaceId}/canvases/${canvasId}/permissions/${userId}/status`,
      { status }
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

/** Update paper status for a user. */
export const updatePaperStatus = async (
  workspaceId: string,
  paperId: string,
  userId: string,
  status: ResourceStatus
): Promise<ApiResult<void>> => {
  try {
    await apiClient.patch(
      `/workspaces/${workspaceId}/papers/${paperId}/permissions/${userId}/status`,
      { status }
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

export const getPapers = async (
  workspaceId: string
): Promise<ApiResult<Paper[]>> => {
  try {
    const { data } = await apiClient.get<Paper[]>(
      `/workspaces/${workspaceId}/papers`
    );
    return { success: true, data };
  } catch (error) {
    return handleError(error);
  }
};

// Default empty Lexical editor state wrapped in PaperData structure
const DEFAULT_PAPER_DATA = JSON.stringify({
  editorState: {
    root: {
      children: [
        {
          children: [],
          direction: null,
          format: "",
          indent: 0,
          type: "paragraph",
          version: 1,
        },
      ],
      direction: null,
      format: "",
      indent: 0,
      type: "root",
      version: 1,
    },
  },
});

const DEFAULT_CANVAS_DATA = JSON.stringify({ rectangles: [], scale: 1 });

export const createCanvas = async (
  workspaceId: string,
  data: Partial<CreateCanvasRequest> & Pick<CreateCanvasRequest, "canvas_name">
): Promise<ApiResult<Canvas>> => {
  try {
    const requestPayload: CreateCanvasRequest = {
      canvas_name: data.canvas_name,
      canvas_data: data.canvas_data ?? DEFAULT_CANVAS_DATA,
      description: data.description ?? null,
      instructions: data.instructions ?? null,
      due_date: data.due_date ?? null,
      required: data.required ?? false,
      assign_to_all: data.assign_to_all ?? true,
      assign_to_users: data.assign_to_users ?? [],
    };
    const response = await apiClient.post<Canvas>(
      `/workspaces/${workspaceId}/canvases`,
      requestPayload
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export const getWorkspaceCanvasesManage = async (
  workspaceId: string
): Promise<ApiResult<WorkspaceCanvasView[]>> => {
  try {
    const response = await apiClient.get<{
      canvases: WorkspaceCanvasView[] | null;
    }>(`/workspaces/${workspaceId}/canvases/manage`);
    return { success: true, data: response.data.canvases ?? [] };
  } catch (error) {
    return handleError(error);
  }
};

export const getWorkspacePapersManage = async (
  workspaceId: string
): Promise<ApiResult<WorkspacePaperView[]>> => {
  try {
    const response = await apiClient.get<{
      papers: WorkspacePaperView[] | null;
    }>(`/workspaces/${workspaceId}/papers/manage`);
    return { success: true, data: response.data.papers ?? [] };
  } catch (error) {
    return handleError(error);
  }
};

export const renameCanvas = async (
  workspaceId: string,
  canvasId: string,
  canvasName: string
): Promise<ApiResult<void>> => {
  try {
    await apiClient.put<void>(
      `/workspaces/${workspaceId}/canvases/${canvasId}`,
      { canvas_name: canvasName }
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

export const deleteCanvas = async (
  workspaceId: string,
  canvasId: string
): Promise<ApiResult<void>> => {
  try {
    await apiClient.delete<void>(
      `/workspaces/${workspaceId}/canvases/${canvasId}`
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

export const renamePaper = async (
  workspaceId: string,
  paperId: string,
  paperName: string
): Promise<ApiResult<void>> => {
  try {
    await apiClient.put<void>(
      `/workspaces/${workspaceId}/papers/${paperId}`,
      { paper_name: paperName }
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

export const deletePaper = async (
  workspaceId: string,
  paperId: string
): Promise<ApiResult<void>> => {
  try {
    await apiClient.delete<void>(
      `/workspaces/${workspaceId}/papers/${paperId}`
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

export const createPaper = async (
  workspaceId: string,
  data: Partial<CreatePaperRequest> & Pick<CreatePaperRequest, "paper_name">
): Promise<ApiResult<Paper>> => {
  try {
    // Fill in defaults for required fields
    const requestPayload: CreatePaperRequest = {
      paper_name: data.paper_name,
      paper_data: data.paper_data ?? DEFAULT_PAPER_DATA,
      description: data.description ?? null,
      instructions: data.instructions ?? null,
      due_date: data.due_date ?? null,
      required: data.required ?? true,
      assign_to_all: data.assign_to_all ?? false,
      assign_to_users: data.assign_to_users ?? [],
    };
    const response = await apiClient.post<Paper>(
      `/workspaces/${workspaceId}/papers`,
      requestPayload
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export const updateWorkspace = async (
  workspaceId: string,
  data: Partial<UpdateWorkspaceRequest>
): Promise<ApiResult<void>> => {
  try {
    await apiClient.put<void>(`/workspaces/${workspaceId}`, data);
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Delete the workspace and cascade-clean its resources. Owner-gated on the
 * backend. After a successful delete the caller is responsible for picking
 * the user a new active workspace and navigating away from any page that
 * referenced the deleted one.
 */
export const deleteWorkspace = async (
  workspaceId: string
): Promise<ApiResult<void>> => {
  try {
    await apiClient.delete<void>(`/workspaces/${workspaceId}`);
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

export const updateReading = async (
  workspaceId: string,
  readingId: string,
  highlights: IHighlight[]
): Promise<ApiResult<void>> => {
  try {
    await apiClient.put<void>(
      `/workspaces/${workspaceId}/readings/${readingId}`,
      {
        reading_data: JSON.stringify({ highlights }),
      }
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

// Instructor gradebook (reading/canvas/paper). Path segment differs by
// type. Paper is manual-grade only (no student-side assessment), but the
// gradebook read/override path is identical.
type GradableType = "reading" | "canvas" | "paper";
const gradeSeg = (t: GradableType) =>
  t === "reading" ? "readings" : t === "canvas" ? "canvases" : "papers";

export const getResourceGrades = async (
  type: GradableType,
  workspaceId: string,
  resourceId: string
): Promise<ApiResult<ResourceGrades>> => {
  try {
    const { data } = await apiClient.get<ResourceGrades>(
      `/workspaces/${workspaceId}/${gradeSeg(type)}/${resourceId}/grades`
    );
    return { success: true, data };
  } catch (error) {
    return handleError(error);
  }
};

export const setStudentGrade = async (
  type: GradableType,
  workspaceId: string,
  resourceId: string,
  studentId: string,
  body: { points: number | null; released: boolean }
): Promise<ApiResult<void>> => {
  try {
    await apiClient.put(
      `/workspaces/${workspaceId}/${gradeSeg(type)}/${resourceId}/grades/${studentId}`,
      body
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

// Phase 6 — per-student workspace gradebook: one student's grade on
// every resource + their manual bonus. The transpose of getResourceGrades.
export const getStudentGradebook = async (
  workspaceId: string,
  userId: string
): Promise<ApiResult<WorkspaceStudentGradebook>> => {
  try {
    const { data } = await apiClient.get<WorkspaceStudentGradebook>(
      `/workspaces/${workspaceId}/students/${userId}/grades`
    );
    return { success: true, data };
  } catch (error) {
    return handleError(error);
  }
};

// Release every graded resource for a student in a workspace at once.
export const releaseAllForStudent = async (
  workspaceId: string,
  userId: string
): Promise<ApiResult<void>> => {
  try {
    await apiClient.put(
      `/workspaces/${workspaceId}/students/${userId}/release-all`,
      {}
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

// Override (or clear, with total=null) a student's final workspace grade.
export const setStudentOverride = async (
  workspaceId: string,
  userId: string,
  total: number | null
): Promise<ApiResult<void>> => {
  try {
    await apiClient.put(
      `/workspaces/${workspaceId}/students/${userId}/override`,
      { total }
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

// The requesting student's own workspace gradebook (released grades
// only — unreleased redacted server-side). For the student "grade so far".
export const getMyWorkspaceGrade = async (
  workspaceId: string
): Promise<ApiResult<WorkspaceStudentGradebook>> => {
  try {
    const { data } = await apiClient.get<WorkspaceStudentGradebook>(
      `/workspaces/${workspaceId}/my-grade`
    );
    return { success: true, data };
  } catch (error) {
    return handleError(error);
  }
};

// Set a resource's relative weight in the workspace gradebook total.
export const setResourceWeight = async (
  workspaceId: string,
  resourceType: "reading" | "canvas" | "paper",
  resourceId: string,
  weight: number
): Promise<ApiResult<void>> => {
  try {
    await apiClient.put(`/workspaces/${workspaceId}/gradebook/weight`, {
      resource_type: resourceType,
      resource_id: resourceId,
      weight,
    });
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

export interface AssignResourcePayload {
  dueDate?: string | null;
  assignmentKind?: "optional" | "required" | "graded";
}

/**
 * Instructor "Assign" — set due date and/or assignment kind on a
 * resource. Reading uses its top-level metadata PUT; canvas/paper use
 * their workspace-nested metadata PATCH. Owner-gated server-side.
 */
export const assignResource = async (
  type: "reading" | "canvas" | "paper",
  workspaceId: string,
  resourceId: string,
  payload: AssignResourcePayload
): Promise<ApiResult<void>> => {
  const body: Record<string, unknown> = {};
  if (payload.dueDate !== undefined) body.due_date = payload.dueDate;
  if (payload.assignmentKind !== undefined) {
    body.assignment_kind = payload.assignmentKind;
  }
  try {
    if (type === "reading") {
      await apiClient.put(`/readings/${resourceId}`, body);
    } else if (type === "canvas") {
      await apiClient.patch(
        `/workspaces/${workspaceId}/canvases/${resourceId}/metadata`,
        body
      );
    } else {
      await apiClient.patch(
        `/workspaces/${workspaceId}/papers/${resourceId}/metadata`,
        body
      );
    }
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

export const updatePaper = async (
  workspaceId: string,
  paperId: string,
  paperData: unknown
): Promise<ApiResult<void>> => {
  try {
    // paperData should already be a JSON string from Lexical, but handle both cases
    const paper_data =
      typeof paperData === "string" ? paperData : JSON.stringify(paperData);

    await apiClient.put<void>(`/workspaces/${workspaceId}/papers/${paperId}`, {
      paper_data,
    });
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Update a document's metadata. Used by the DocumentsTable inline rename.
 * documentUrl must be passed alongside the new name because the backend's
 * PUT handler overwrites both columns unconditionally — sending the
 * existing url keeps it stable.
 */
export const updateDocument = async (
  documentId: string,
  documentName: string,
  documentUrl: string | null
): Promise<ApiResult<void>> => {
  try {
    await apiClient.put<void>(`/documents/${documentId}`, {
      document_name: documentName,
      document_url: documentUrl ?? "",
    });
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

export const deleteDocuments = async (
  documentIds: string[]
): Promise<ApiResult<void>> => {
  try {
    await Promise.all(
      documentIds.map((id) => apiClient.delete<void>(`/documents/${id}`))
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Pre-delete dependency check for a document. Returns the readings that
 * would be cascaded out if the document were deleted. The DocumentsTable
 * uses this to surface impact in the delete confirmation.
 */
export const getDocumentDependents = async (
  documentId: string
): Promise<ApiResult<DocumentDependent[]>> => {
  try {
    const response = await apiClient.get<{ readings: DocumentDependent[] | null }>(
      `/documents/${documentId}/dependents`
    );
    return { success: true, data: response.data.readings ?? [] };
  } catch (error) {
    return handleError(error);
  }
};

// Per-file ceiling enforced on the client. The backend enforces the
// same limit (constants.MaxDocumentSizeBytes); keep these in sync.
// ---- MFA recovery codes -------------------------------------------------

export interface RecoveryCodesResponse {
  codes: string[];
  count: number;
}

export interface RecoveryCodesStatusResponse {
  remaining: number;
}

/**
 * Generate (or regenerate) recovery codes. Returns the 10 plaintext
 * codes exactly once; afterward only the count is observable. The
 * server requires AAL2 on this route, so the caller must have just
 * verified their TOTP factor or otherwise stepped up.
 */
export const generateRecoveryCodes = async (): Promise<
  ApiResult<RecoveryCodesResponse>
> => {
  try {
    const response = await apiClient.post<RecoveryCodesResponse>(
      "/auth/mfa/recovery-codes"
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

/** Remaining unused recovery codes for the current user. AAL1-OK. */
export const getRecoveryCodesStatus = async (): Promise<
  ApiResult<RecoveryCodesStatusResponse>
> => {
  try {
    const response = await apiClient.get<RecoveryCodesStatusResponse>(
      "/auth/mfa/recovery-codes/status"
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Submit a plaintext recovery code. On success the server validates
 * the code, marks it used, deletes the user's TOTP factors, and wipes
 * the remaining codes — a one-shot 2FA reset. The caller should then
 * refresh the Supabase session so AAL/factor state is in sync; with
 * no factors enrolled the AAL gate no longer applies.
 */
export interface RedeemRecoveryCodeResponse {
  factor_reset: boolean;
}

export const redeemRecoveryCode = async (
  code: string
): Promise<ApiResult<RedeemRecoveryCodeResponse>> => {
  try {
    const response = await apiClient.post<RedeemRecoveryCodeResponse>(
      "/auth/mfa/redeem-recovery-code",
      { code }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Wipe the current user's recovery codes. Called immediately after
 * a successful supabase.auth.mfa.unenroll so the orphaned hashes
 * don't linger. AAL2-gated.
 */
export const deleteRecoveryCodes = async (): Promise<ApiResult<void>> => {
  try {
    await apiClient.delete("/auth/mfa/recovery-codes");
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Irreversibly delete the current user's account. Cascades through
 * the app DB and removes the Supabase auth.users row. AAL2-gated for
 * MFA-enrolled users — the apiClient interceptor surfaces the step-up
 * dialog if needed. Replaces the legacy supabase.rpc call that was
 * never defined in the project's Supabase database.
 */
export const deleteCurrentUser = async (): Promise<ApiResult<void>> => {
  try {
    await apiClient.delete("/users/me");
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

// ---- Document upload --------------------------------------------------

export const MAX_DOCUMENT_SIZE_BYTES = 100 * 1024 * 1024;

export interface UploadResult {
  documentId: string;
  // True when the backend short-circuited because the same content
  // (file hash or URL) is already in the user's library. No bytes were
  // uploaded; the existing document is reused.
  reused: boolean;
  // Backend-stored name on the reused document. Useful when surfacing
  // "Reused 'Old Title'" instead of the user's about-to-be-discarded
  // input.
  existingName?: string;
}

// SHA-256 of the file body as a lower-hex string. Web Crypto requires
// the full buffer up front — for the 100 MB ceiling this is acceptable
// (~few hundred ms on a modern laptop), and saves us a streaming hash
// dependency.
const sha256Hex = async (file: Blob): Promise<string> => {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

export const uploadFile = async (
  fileName: string,
  file: File,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal
): Promise<ApiResult<UploadResult>> => {
  try {
    // 1. Hash the file body so the backend can short-circuit a
    // re-upload of the same content. Done first so we don't issue a
    // presigned URL we won't use.
    const contentHash = await sha256Hex(file);
    if (signal?.aborted) throw new Error("Cancelled");

    // 2. Create document record. The backend may return reused=true
    // (no upload_url) if the (owner, content_hash) pair already
    // exists.
    const createDocResponse = await apiClient.post<{
      document: Document;
      upload_url: string;
      reused: boolean;
    }>(
      "/documents",
      {
        document_name: fileName,
        content_hash: contentHash,
        file_size_bytes: file.size,
      },
      { signal }
    );

    const documentId = createDocResponse.data.document.document_id;
    const uploadUrl = createDocResponse.data.upload_url;
    const reused = Boolean(createDocResponse.data.reused);

    // 3. If the backend reused an existing document, skip the S3 PUT
    // entirely — the bytes are already there.
    if (reused) {
      return {
        success: true,
        data: {
          documentId,
          reused: true,
          existingName: createDocResponse.data.document.document_name,
        },
      };
    }

    // 4. Upload file to presigned URL. axios fires onUploadProgress as
    // the request body is sent; for an S3 PUT this is real bytes-on-the-
    // wire, not just the local buffer flush.
    await axios.put(uploadUrl, file, {
      headers: { "Content-Type": file.type },
      signal,
      onUploadProgress: onProgress
        ? (e) => {
            if (e.total) {
              onProgress(Math.round((e.loaded / e.total) * 100));
            }
          }
        : undefined,
    });

    return {
      success: true,
      data: { documentId, reused: false },
    };
  } catch (error) {
    return handleError(error);
  }
};

export const uploadUrl = async (
  fileName: string,
  url: string,
  signal?: AbortSignal
): Promise<ApiResult<UploadResult>> => {
  try {
    const response = await apiClient.post<{
      document: Document;
      upload_url: string;
      reused: boolean;
    }>(
      "/documents",
      {
        document_name: fileName,
        document_url: url,
      },
      { signal }
    );
    const reused = Boolean(response.data.reused);
    return {
      success: true,
      data: {
        documentId: response.data.document.document_id,
        reused,
        existingName: reused
          ? response.data.document.document_name
          : undefined,
      },
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getPersonalUrl = async (
  documentId: string
): Promise<ApiResult<string>> => {
  try {
    const response = await apiClient.get<Document>(`/documents/${documentId}`);
    return { success: true, data: response.data.document_url || "" };
  } catch (error) {
    return handleError(error);
  }
};

type ReadingResponse = Omit<Reading, "reading_data"> & {
  reading_data: IHighlight[];
};

export const loadReadingData = async (
  workspaceId: string,
  readingId: string
): Promise<ApiResult<IHighlight[]>> => {
  try {
    const response = await apiClient.get<ReadingResponse>(
      `/workspaces/${workspaceId}/readings/${readingId}`
    );
    return { success: true, data: response.data.reading_data || [] };
  } catch (error) {
    return handleError(error);
  }
};

type CanvasResponse = Omit<Canvas, "canvas_data"> & {
  canvas_data: CanvasState | null;
};

export const loadCanvasData = async (
  workspaceId: string,
  canvasId: string
): Promise<ApiResult<CanvasState | null>> => {
  try {
    const response = await apiClient.get<CanvasResponse>(
      `/workspaces/${workspaceId}/canvases/${canvasId}`
    );
    return { success: true, data: response.data.canvas_data };
  } catch (error) {
    return handleError(error);
  }
};

type PaperResponse = Omit<Paper, "paper_data"> & {
  paper_data: unknown;
};

export const loadPaperData = async (
  workspaceId: string,
  paperId: string
): Promise<ApiResult<unknown>> => {
  try {
    const response = await apiClient.get<PaperResponse>(
      `/workspaces/${workspaceId}/papers/${paperId}`
    );
    return { success: true, data: response.data.paper_data };
  } catch (error) {
    return handleError(error);
  }
};

export const getAllDocuments = async (
  workspaceId?: string
): Promise<ApiResult<DocumentWithOwner[]>> => {
  try {
    // Always request the WithOwner shape so every caller gets the
    // workspaces[] array on each row. workspace_id is preserved on the
    // query for back-compat but the backend no longer filters by it.
    const url = workspaceId
      ? `/documents?workspace_id=${workspaceId}&with_owner=true`
      : "/documents?with_owner=true";
    const response = await apiClient.get<DocumentWithOwner[]>(url);
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export const setTimeLog = async (
  workspaceId: string,
  timeInput: number
): Promise<ApiResult<void>> => {
  try {
    await updateWorkspace(workspaceId, { user_data: { timeLog: timeInput } });
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

export const getReadingTimeSessions = async (
  workspaceId: string,
  readingId: string
): Promise<ApiResult<TimeSessionsResponse>> => {
  try {
    const response = await apiClient.get<TimeSessionsResponse>(
      `/workspaces/${workspaceId}/readings/${readingId}/time-sessions`
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

// Auto-save of the in-progress session (called on pause + periodically
// while the timer runs). Finalizing via logReadingTimeSession clears it.
export const saveActiveTimeSession = async (
  workspaceId: string,
  readingId: string,
  session: LogTimeSessionRequest
): Promise<ApiResult<void>> => {
  try {
    await apiClient.put<void>(
      `/workspaces/${workspaceId}/readings/${readingId}/time-sessions/active`,
      session
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

export const clearActiveTimeSession = async (
  workspaceId: string,
  readingId: string
): Promise<ApiResult<void>> => {
  try {
    await apiClient.delete<void>(
      `/workspaces/${workspaceId}/readings/${readingId}/time-sessions/active`
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

// Rename one logged session, addressed by array index (stable: the
// array is append-only). Returns the full updated list.
export const renameTimeSession = async (
  workspaceId: string,
  readingId: string,
  index: number,
  label: string
): Promise<ApiResult<TimeSessionsResponse>> => {
  try {
    const response = await apiClient.patch<TimeSessionsResponse>(
      `/workspaces/${workspaceId}/readings/${readingId}/time-sessions/${index}`,
      { label }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export const logReadingTimeSession = async (
  workspaceId: string,
  readingId: string,
  session: LogTimeSessionRequest
): Promise<ApiResult<TimeSessionsResponse>> => {
  try {
    const response = await apiClient.post<TimeSessionsResponse>(
      `/workspaces/${workspaceId}/readings/${readingId}/time-sessions`,
      session
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

// ============================================================================
// Annotations (Item 7, Stage 3.6)
//
// Per-user annotation CRUD on the annotations table. Replaces the
// legacy readings.reading_data JSONB blob — see annotations migration
// 000016 and the AnnotationStore.
// ============================================================================

export const getAnnotations = async (
  workspaceId: string,
  readingId: string,
  // Stage 3.7: when set AND the requester is the workspace owner, returns
  // that user's annotations instead of the requester's own. Backend
  // returns 403 otherwise.
  userId?: string
): Promise<ApiResult<Annotation[]>> => {
  try {
    const url = `/workspaces/${workspaceId}/readings/${readingId}/annotations${
      userId ? `?user_id=${encodeURIComponent(userId)}` : ""
    }`;
    const response = await apiClient.get<{ annotations: Annotation[] }>(url);
    return { success: true, data: response.data.annotations ?? [] };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * List contributors on a reading with their annotation counts (Stage 3.7).
 * Owner-only — non-owners get 403.
 */
export interface ReadingContributor {
  user_id: string;
  username: string;
  annotation_count: number;
}
export const getReadingContributors = async (
  workspaceId: string,
  readingId: string
): Promise<ApiResult<ReadingContributor[]>> => {
  try {
    const response = await apiClient.get<{ contributors: ReadingContributor[] }>(
      `/workspaces/${workspaceId}/readings/${readingId}/contributors`
    );
    return { success: true, data: response.data.contributors ?? [] };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Stage 6.8: parallel of getReadingContributors for the canvas
 * grading view. rectangle_count is jsonb_array_length on the
 * contributor's canvas_user_data.canvas_data.rectangles.
 */
export interface CanvasContributor {
  user_id: string;
  username: string;
  rectangle_count: number;
}
export const getCanvasContributors = async (
  workspaceId: string,
  canvasId: string
): Promise<ApiResult<CanvasContributor[]>> => {
  try {
    const response = await apiClient.get<{ contributors: CanvasContributor[] }>(
      `/workspaces/${workspaceId}/canvases/${canvasId}/contributors`
    );
    return { success: true, data: response.data.contributors ?? [] };
  } catch (error) {
    return handleError(error);
  }
};

export const createAnnotation = async (
  workspaceId: string,
  readingId: string,
  body: CreateAnnotationRequest
): Promise<ApiResult<Annotation>> => {
  try {
    const response = await apiClient.post<{ annotation: Annotation }>(
      `/workspaces/${workspaceId}/readings/${readingId}/annotations`,
      body
    );
    return { success: true, data: response.data.annotation };
  } catch (error) {
    return handleError(error);
  }
};

export const updateAnnotation = async (
  workspaceId: string,
  readingId: string,
  annotationClientId: string,
  body: UpdateAnnotationRequest
): Promise<ApiResult<Annotation>> => {
  try {
    const response = await apiClient.patch<{ annotation: Annotation }>(
      `/workspaces/${workspaceId}/readings/${readingId}/annotations/${annotationClientId}`,
      body
    );
    return { success: true, data: response.data.annotation };
  } catch (error) {
    return handleError(error);
  }
};

export const deleteAnnotation = async (
  workspaceId: string,
  readingId: string,
  annotationClientId: string
): Promise<ApiResult<void>> => {
  try {
    await apiClient.delete(
      `/workspaces/${workspaceId}/readings/${readingId}/annotations/${annotationClientId}`
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Add a user to a workspace by user_id (Item 7, Stage 3.5). Dev-only —
 * a production-ready flow would be invite-based; this silently grants
 * access without recipient consent. See actionplan.org for the
 * replacement plan.
 *
 * Resolves to a 409 AppError if the user is already a member.
 */
/**
 * Add a user to a workspace. Pass either an email or a user_id (exactly
 * one) — email is the preferred path and matches the dialog UX; user_id
 * is kept as a fallback for accounts that exist before the email column
 * was backfilled. The backend rejects requests with both or neither.
 *
 * Resolves to a 404 AppError if the email doesn't match any account, or
 * a 409 if the user is already a member.
 */
export const addWorkspaceMember = async (
  workspaceId: string,
  identifier: { email: string } | { userId: string },
  role: "contributor" | "owner" = "contributor"
): Promise<ApiResult<void>> => {
  try {
    const body: { email?: string; user_id?: string; role: string } = { role };
    if ("email" in identifier) {
      body.email = identifier.email;
    } else {
      body.user_id = identifier.userId;
    }
    await apiClient.post(`/workspaces/${workspaceId}/members`, body);
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Remove a user from a workspace. The backend refuses (409) when the
 * target is the workspace's last owner, so a workspace can never be left
 * unowned.
 */
export const removeWorkspaceMember = async (
  workspaceId: string,
  userId: string
): Promise<ApiResult<void>> => {
  try {
    await apiClient.delete(`/workspaces/${workspaceId}/members/${userId}`);
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Update a workspace member's role. The backend refuses (409) when the
 * change would demote the last owner.
 */
export const updateWorkspaceMemberRole = async (
  workspaceId: string,
  userId: string,
  role: "contributor" | "owner"
): Promise<ApiResult<void>> => {
  try {
    await apiClient.patch(`/workspaces/${workspaceId}/members/${userId}`, {
      role,
    });
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Fetch the rubric for a reading. Resolves to `null` when no rubric has
 * been created for the reading yet (the backend returns `rubric: null`).
 *
 * Nested under workspaces so the workspace-role authz can run (the
 * reading-role view has no creation path for Owner — see Stage 2 fix).
 */
export const getReadingRubric = async (
  workspaceId: string,
  readingId: string
): Promise<ApiResult<ReadingRubric | null>> => {
  try {
    const response = await apiClient.get<{ rubric: ReadingRubric | null }>(
      `/workspaces/${workspaceId}/readings/${readingId}/rubric`
    );
    return { success: true, data: response.data.rubric };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Fetch the current user's reading assessment, or another user's when
 * `userId` is provided AND the requester is the workspace owner.
 * Resolves to `null` when no record exists yet (the contributor hasn't
 * auto-saved). Backend returns 403 if a contributor tries to read
 * another user's assessment.
 */
export const getReadingAssessment = async (
  workspaceId: string,
  readingId: string,
  userId?: string
): Promise<ApiResult<ReadingAssessment | null>> => {
  try {
    const url = `/workspaces/${workspaceId}/readings/${readingId}/assessment${
      userId ? `?user_id=${encodeURIComponent(userId)}` : ""
    }`;
    const response = await apiClient.get<{ assessment: ReadingAssessment | null }>(url);
    return { success: true, data: response.data.assessment };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Upsert the current user's reading assessment (Stage 5 auto-save).
 * Backend resolves rubric and instructor FKs from the workspace context.
 * Partial — only fields present in the body are written.
 */
export const updateReadingAssessment = async (
  workspaceId: string,
  readingId: string,
  body: UpdateReadingAssessmentRequest
): Promise<ApiResult<ReadingAssessment>> => {
  try {
    const response = await apiClient.put<{ assessment: ReadingAssessment }>(
      `/workspaces/${workspaceId}/readings/${readingId}/assessment`,
      body
    );
    return { success: true, data: response.data.assessment };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Fetch the rubric for a canvas (Item 7, Stage 6.2). Mirrors
 * getReadingRubric. Resolves to `null` when no rubric exists yet.
 */
export const getCanvasRubric = async (
  workspaceId: string,
  canvasId: string
): Promise<ApiResult<CanvasRubric | null>> => {
  try {
    const response = await apiClient.get<{ rubric: CanvasRubric | null }>(
      `/workspaces/${workspaceId}/canvases/${canvasId}/rubric`
    );
    return { success: true, data: response.data.rubric };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Stage 6.6: canvas assessment GET. Returns the current user's record
 * by default, or another user's when userId is provided AND the
 * requester is the workspace owner. Null when no record exists yet.
 */
export const getCanvasAssessment = async (
  workspaceId: string,
  canvasId: string,
  userId?: string
): Promise<ApiResult<CanvasAssessment | null>> => {
  try {
    const url = `/workspaces/${workspaceId}/canvases/${canvasId}/assessment${
      userId ? `?user_id=${encodeURIComponent(userId)}` : ""
    }`;
    const response = await apiClient.get<{ assessment: CanvasAssessment | null }>(url);
    return { success: true, data: response.data.assessment };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Stage 6.6: canvas assessment PUT (contributor auto-save).
 */
export const updateCanvasAssessment = async (
  workspaceId: string,
  canvasId: string,
  body: UpdateCanvasAssessmentRequest
): Promise<ApiResult<CanvasAssessment>> => {
  try {
    const response = await apiClient.put<{ assessment: CanvasAssessment }>(
      `/workspaces/${workspaceId}/canvases/${canvasId}/assessment`,
      body
    );
    return { success: true, data: response.data.assessment };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Stage 6.7: per-user canvas data GET. Returns the current user's
 * canvas blob by default, or another user's when userId is provided
 * AND the requester is the workspace owner. Null when the row exists
 * but has no canvas_data yet (status==='assigned' with no save) or
 * when no row exists at all.
 */
export const getCanvasUserData = async (
  workspaceId: string,
  canvasId: string,
  userId?: string
): Promise<ApiResult<CanvasUserData | null>> => {
  try {
    const url = `/workspaces/${workspaceId}/canvases/${canvasId}/data${
      userId ? `?user_id=${encodeURIComponent(userId)}` : ""
    }`;
    const response = await apiClient.get<{ canvas_user_data: CanvasUserData | null }>(url);
    return { success: true, data: response.data.canvas_user_data };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Stage 6.7: per-user canvas data PUT (contributor auto-save).
 * canvasData is stringified at the call site to match the legacy
 * updateCanvas payload shape (canvas_data is JSON-encoded text the
 * backend casts to jsonb).
 */
export const updateCanvasUserData = async (
  workspaceId: string,
  canvasId: string,
  canvasData: CanvasData
): Promise<ApiResult<CanvasUserData>> => {
  try {
    const response = await apiClient.put<{ canvas_user_data: CanvasUserData }>(
      `/workspaces/${workspaceId}/canvases/${canvasId}/data`,
      { canvas_data: JSON.stringify(canvasData) }
    );
    return { success: true, data: response.data.canvas_user_data };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Upsert the rubric for a canvas. Workspace-owner-only; non-owners
 * get 403. Partial body supported.
 */
export const updateCanvasRubric = async (
  workspaceId: string,
  canvasId: string,
  body: UpdateCanvasRubricRequest
): Promise<ApiResult<CanvasRubric>> => {
  try {
    const response = await apiClient.put<{ rubric: CanvasRubric }>(
      `/workspaces/${workspaceId}/canvases/${canvasId}/rubric`,
      body
    );
    return { success: true, data: response.data.rubric };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Upsert the rubric for a reading. Partial — only fields present in the
 * body are written. Workspace-owner-only; non-owners get 403.
 */
export const updateReadingRubric = async (
  workspaceId: string,
  readingId: string,
  body: UpdateReadingRubricRequest
): Promise<ApiResult<ReadingRubric>> => {
  try {
    const response = await apiClient.put<{ rubric: ReadingRubric }>(
      `/workspaces/${workspaceId}/readings/${readingId}/rubric`,
      body
    );
    return { success: true, data: response.data.rubric };
  } catch (error) {
    return handleError(error);
  }
};

/** Creates a user in the app database after Supabase signup */
export const createUser = async (
  userData: CreateUserRequest
): Promise<ApiResult<User>> => {
  try {
    const response = await apiClient.post<User>("/users", userData);
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

interface UserInfo {
  user_id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  tier: string;
  tier_source: string;
}

/** Get current user info from Go API */
export const getCurrentUserInfo = async (): Promise<ApiResult<UserInfo>> => {
  try {
    const response = await apiClient.get<{
      user_id: string;
      username: string;
      full_name: string | null;
      avatar_url: string | null;
      created_at: string;
      tier: string;
      tier_source: string;
    }>("/users/me");

    return {
      success: true,
      data: {
        user_id: response.data.user_id,
        username: response.data.username,
        full_name: response.data.full_name,
        avatar_url: response.data.avatar_url,
        tier: response.data.tier,
        tier_source: response.data.tier_source,
      },
    };
  } catch (error) {
    return handleError(error);
  }
};

// Update the current user's profile (username / full name / avatar) via the
// Go API. Replaces the old direct-Supabase table writes, which failed
// because the app's users table lives in the API's database, not the
// Supabase project's PostgREST. Any subset of fields may be provided.
export const updateCurrentUser = async (payload: {
  username?: string;
  full_name?: string;
  avatar_url?: string;
}): Promise<ApiResult<void>> => {
  try {
    await apiClient.put("/users/me", payload);
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

// Upload a new avatar image (PNG/JPEG). Mirrors the document flow: ask the
// API for a presigned PUT URL, push the bytes straight to S3 (no auth
// header to S3), then persist the object key on the profile. The stored
// key is presigned back to a URL on read.
export const uploadAvatar = async (file: File): Promise<ApiResult<void>> => {
  try {
    const presign = await apiClient.post<{
      upload_url: string;
      object_key: string;
    }>("/users/me/avatar", { content_type: file.type });

    await axios.put(presign.data.upload_url, file, {
      headers: { "Content-Type": file.type },
    });

    await apiClient.put("/users/me", { avatar_url: presign.data.object_key });
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

// Onboarding state: a per-user map keyed by flow id ("tour",
// "hint:canvas-drag", …), stored server-side for signed-in users.
// Demo/anonymous visitors never call these — their state lives in the
// "onboarding.state" IndexedDB key via the persistence middleware.
export const getOnboardingState = async (): Promise<
  ApiResult<OnboardingStateResponse>
> => {
  try {
    const response = await apiClient.get<OnboardingStateResponse>(
      "/users/me/onboarding"
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

// Merges entries key-by-key: a named key is replaced wholesale,
// unnamed keys survive. Returns the full resulting map.
export const patchOnboardingState = async (
  entries: UpdateOnboardingStateRequest["entries"]
): Promise<ApiResult<OnboardingStateResponse>> => {
  try {
    const response = await apiClient.patch<OnboardingStateResponse>(
      "/users/me/onboarding",
      { entries }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

// Organization endpoints (Phase 3).
export const getCurrentUserOrgs = async (): Promise<
  ApiResult<OrganizationWithRole[]>
> => {
  try {
    const response = await apiClient.get<OrganizationWithRole[]>(
      "/users/me/orgs"
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export const getOrganizationBySlug = async (
  slug: string
): Promise<ApiResult<Organization>> => {
  try {
    const response = await apiClient.get<Organization>(`/orgs/${slug}`);
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export const getOrganizationMembers = async (
  slug: string
): Promise<ApiResult<OrganizationMembershipWithUser[]>> => {
  try {
    const response = await apiClient.get<OrganizationMembershipWithUser[]>(
      `/orgs/${slug}/members`
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

// Platform-admin only.
export const createOrganization = async (
  name: string,
  slug: string,
  emailDomains: string[] = []
): Promise<ApiResult<Organization>> => {
  try {
    const response = await apiClient.post<Organization>(
      "/admin/organizations",
      { name, slug, email_domains: emailDomains }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

// Member management (Phase 4). All three require org_admin role,
// except DELETE which a member can call on themselves (self-leave).
//
// addOrgMember takes either email or user_id (exactly one) — same shape
// as addWorkspaceMember. Email is the preferred path; user_id stays as
// a fallback for accounts that exist before the email column was
// backfilled.
export const addOrgMember = async (
  slug: string,
  identifier: { email: string } | { userId: string },
  role?: "member" | "org_admin"
): Promise<ApiResult<unknown>> => {
  try {
    const body: { email?: string; user_id?: string; role?: string } = {};
    if ("email" in identifier) {
      body.email = identifier.email;
    } else {
      body.user_id = identifier.userId;
    }
    if (role) body.role = role;
    const response = await apiClient.post(`/orgs/${slug}/members`, body);
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export const updateOrgMember = async (
  slug: string,
  userId: string,
  patch: { role?: "member" | "org_admin"; tier?: "annotator" | "evaluator" }
): Promise<ApiResult<unknown>> => {
  try {
    const response = await apiClient.patch(
      `/orgs/${slug}/members/${userId}`,
      patch
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export const removeOrgMember = async (
  slug: string,
  userId: string
): Promise<ApiResult<void>> => {
  try {
    await apiClient.delete(`/orgs/${slug}/members/${userId}`);
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

// Invite-link onboarding (Phase 5). Create/list/revoke are org_admin
// only; redeem is any authenticated user.
export const createOrgInviteToken = async (
  slug: string,
  expiresInDays: number
): Promise<ApiResult<OrganizationInviteToken>> => {
  try {
    const response = await apiClient.post<OrganizationInviteToken>(
      `/orgs/${slug}/invite-tokens`,
      { expires_in_days: expiresInDays }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export const listOrgInviteTokens = async (
  slug: string
): Promise<ApiResult<OrganizationInviteToken[]>> => {
  try {
    const response = await apiClient.get<OrganizationInviteToken[]>(
      `/orgs/${slug}/invite-tokens`
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export const revokeOrgInviteToken = async (
  slug: string,
  token: string
): Promise<ApiResult<void>> => {
  try {
    await apiClient.delete(`/orgs/${slug}/invite-tokens/${token}`);
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

export const redeemOrgInviteToken = async (
  token: string
): Promise<ApiResult<InviteTokenRedemption>> => {
  try {
    const response = await apiClient.post<InviteTokenRedemption>(
      `/orgs/join/${token}`
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

// Workspace invite tokens (Phase 5 of admin panel rebuild). Mirrors the
// org invite-link API; owner-only create/list/revoke, any authenticated
// user can redeem.
export const createWorkspaceInviteToken = async (
  workspaceId: string,
  expiresInDays: number
): Promise<ApiResult<WorkspaceInviteToken>> => {
  try {
    const response = await apiClient.post<WorkspaceInviteToken>(
      `/workspaces/${workspaceId}/invite-tokens`,
      { expires_in_days: expiresInDays }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export const listWorkspaceInviteTokens = async (
  workspaceId: string
): Promise<ApiResult<WorkspaceInviteToken[]>> => {
  try {
    const response = await apiClient.get<WorkspaceInviteToken[]>(
      `/workspaces/${workspaceId}/invite-tokens`
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};

export const revokeWorkspaceInviteToken = async (
  workspaceId: string,
  token: string
): Promise<ApiResult<void>> => {
  try {
    await apiClient.delete(
      `/workspaces/${workspaceId}/invite-tokens/${token}`
    );
    return { success: true, data: undefined };
  } catch (error) {
    return handleError(error);
  }
};

export const redeemWorkspaceInviteToken = async (
  token: string
): Promise<ApiResult<WorkspaceInviteRedemption>> => {
  try {
    const response = await apiClient.post<WorkspaceInviteRedemption>(
      `/workspaces/join/${token}`
    );
    return { success: true, data: response.data };
  } catch (error) {
    return handleError(error);
  }
};
